import { backendLabelOf } from '@littlebird/ui-utils';

import { userStore } from '@/stores/user.store';

import {
  type OpenWorkElsewhere,
  useBackendChoiceQuery,
  useOpenWorkElsewhereQuery,
} from '../backend-choice.query';

/** The staff Backend setting: the server's choice and which backend answered. Staff stay on TypeScript, so it only shows */
export const useBackendChoiceSetting = () => {
  const choice = useBackendChoiceQuery();
  const answer = choice.data;

  return {
    loadFailed: choice.isError,
    retryLoad: () => void choice.refetch(),
    // The cached profile, not the query: a change made elsewhere reaches it
    // through main, and the server's answer to this page is applied to it
    usesTypeScript:
      answer !== undefined && userStore.backendChoice === 'typescript',
    servedByLine: answer
      ? answer.servedBy
        ? `Last response served by: ${answer.servedBy}`
        : 'Last response did not report which backend served it'
      : null,
  };
};

const SHOWN_ITEMS = 5;

const CLUSTER_NAMES: Readonly<Record<string, string>> = {
  self_hosted: 'the self-hosted Temporal cluster',
  cloud: 'Temporal Cloud',
};

const titleOf = (title: string | null) => title || 'Untitled chat';

export interface OpenWorkView {
  title: string;
  // The chats, then the turns, then the workflows, the first few of each
  // with a line for how many more each leaves out
  items: string[];
  // A cluster that could not list the user's runs, in a sentence
  clusterNotes: string[];
}

const capped = (lines: string[]): string[] =>
  lines.length > SHOWN_ITEMS
    ? [...lines.slice(0, SHOWN_ITEMS), `and ${lines.length - SHOWN_ITEMS} more`]
    : lines;

/** What still runs on the other backend, for the Backend setting; null when there is nothing to say */
export const openWorkView = (work: OpenWorkElsewhere): OpenWorkView | null => {
  const items = [
    ...capped(
      work.chats.map((chat) => `Chat mid-turn: ${titleOf(chat.chat_title)}`)
    ),
    ...capped(
      work.turns.map(
        (turn) => `Turn ${turn.state}: ${titleOf(turn.chat_title)}`
      )
    ),
    ...capped(
      work.clusters.flatMap((cluster) =>
        cluster.listing === 'listed'
          ? cluster.workflows.map(
              (workflow) => `Workflow: ${workflow.workflow_type}`
            )
          : []
      )
    ),
  ];
  const clusterNotes = work.clusters.flatMap((cluster) => {
    const name = CLUSTER_NAMES[cluster.cluster] ?? cluster.cluster;
    switch (cluster.listing) {
      case 'search_attribute_off':
        return [
          `Workflows on ${name} can't be listed yet: its workflow starts don't carry the user id.`,
        ];
      case 'unreachable':
        return [`Workflows on ${name} couldn't be checked.`];
      case 'listed':
      case 'not_configured':
        return [];
    }
  });
  if (items.length === 0 && clusterNotes.length === 0) return null;
  return {
    title: `Still running on ${backendLabelOf(work.other_backend)}`,
    items,
    clusterNotes,
  };
};

/** The open work on the other backend, once loaded; null while loading, on a failure, or when nothing runs there */
export const useOpenWorkElsewhere = (): OpenWorkView | null => {
  const work = useOpenWorkElsewhereQuery();
  // A failed refetch keeps the last answer's data, which may name the
  // backend the account has just left
  return work.data && !work.isError ? openWorkView(work.data) : null;
};
